import React, { useState, useEffect } from 'react';
import { View, Pressable, Animated, Platform, Alert, ActivityIndicator } from 'react-native';
import { Text } from '@constants/Themed';
import { AntDesign, FontAwesome6, MaterialIcons } from '@expo/vector-icons';
import { Loading } from '@components/Global/Loading';
import {
  getWindowContainerStyles,
  getWindowHeaderStyles,
  getWindowHeaderTitleStyles,
  getWindowCloseButtonStyles,
  getWindowCloseIconColor
} from './utils/windowStyles';
import ErrorBoundary from '@utils/ErrorBoundary';
import { XStack, YStack } from 'tamagui';
import { getApiBaseUrl } from '@api/constant';
import apis, { postDocumentTemplate, postInternalKnowledge } from '@api/api';
import { useSelector } from 'react-redux';
import { DefaultRootState } from '@reduxLocal/persistState';
import { useReactiveWindowDimensions } from '@hooks/useReactiveWindowDimensions';

// Lazy load components
const HierarchyWindow = React.lazy(() => import('./HierarchyWindow'));
const CanvasWindow = React.lazy(() => import('./CanvasWindow'));
const EditorWindow = React.lazy(() => import('./EditorWindow'));
const DataHandlerWindow = React.lazy(() => import('./DataHandlerWindow'));
const DocumentWindow = React.lazy(() => import('./DocumentWindow'));

export type WindowComponentType = 'hierarchy' | 'canvas' | 'editor' | 'dataHandler' | 'document' | null;

interface UnifiedWindowProps {
  theme: boolean;
  screenWidth: number;
  widthAnim: Animated.Value;
  windowWidth: number;
  activeComponent: WindowComponentType;
  isLoading: boolean;
  onClose: () => void;
  navigation?: any; // Navigation prop for saving to templates/knowledge
  // Hierarchy props
  hierarchyProps?: any;
  // Canvas props
  canvasProps?: any;
  // Editor props
  editorProps?: any;
  // DataHandler props
  dataHandlerProps?: any;
  // Document props
  documentProps?: any;
}

const UnifiedWindow: React.FC<UnifiedWindowProps> = ({
  theme,
  widthAnim,
  activeComponent,
  windowWidth,
  isLoading,
  onClose,
  navigation,
  hierarchyProps,
  canvasProps,
  editorProps,
  dataHandlerProps,
  documentProps
}) => {
  const [componentLoaded, setComponentLoaded] = useState<WindowComponentType | null>(null);
  const [formattingLoading, setFormattingLoading] = useState<string | null>(null);
  const [isContractFormatted, setIsContractFormatted] = useState(false);
  const { width: screenWidth } = useReactiveWindowDimensions();
  const [contractData, setContractData] = useState<{ content: string; placeholders: string[] } | null>(null);
  const [saving, setSaving] = useState<string | null>(null);
  const currentUser = useSelector((state: DefaultRootState) => state.promiseData.data);

  // Reset loaded component when active component changes
  useEffect(() => {
    if (activeComponent !== componentLoaded) {
      setComponentLoaded(null);
    }
  }, [activeComponent, componentLoaded]);

  // Reset contract state when editor is closed or component changes
  useEffect(() => {
    if (activeComponent !== 'editor') {
      setIsContractFormatted(false);
      setContractData(null);
    }
  }, [activeComponent]);

  // Debug: Log state changes
  useEffect(() => {
    console.log('🔍 [UnifiedWindow] Contract state changed:', {
      isContractFormatted,
      hasContractData: !!contractData,
      placeholdersCount: contractData?.placeholders?.length || 0,
      activeComponent
    });
  }, [isContractFormatted, contractData, activeComponent]);

  // Simulate lazy loading
  useEffect(() => {
    if (activeComponent && !componentLoaded && !isLoading) {
      const timer = setTimeout(() => {
        setComponentLoaded(activeComponent);
      }, 300);
      return () => clearTimeout(timer);
    }
  }, [activeComponent, componentLoaded, isLoading]);

  const containerStyles = getWindowContainerStyles({
    theme,
    screenWidth,
    widthAnim,
    windowWidth,
    isActive: !!activeComponent // Pass isActive for simpler opacity control
  });

  const getWindowTitle = () => {
    switch (activeComponent) {
      case 'hierarchy':
        return 'Automation Flow Designer';
      case 'canvas':
        return 'Image Canvas Designer';
      case 'editor':
        return 'Rich Text Editor';
      case 'dataHandler':
        return 'Data Handler';
      case 'document':
        return 'Document Editor';
      default:
        return '';
    }
  };

  const handleFormatAsContract = async () => {
    if (!editorProps?.editorRef?.current) {
      Alert.alert('Error', 'Editor is not available');
      return;
    }

    try {
      setFormattingLoading('contract');
      const content = editorProps.editorRef.current.getContent();

      if (!content || content.trim().length < 10) {
        Alert.alert('Error', 'Editor content is too short. Please add some content first.');
        setFormattingLoading(null);
        return;
      }

      const apiBaseUrl = await getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/ai/editor/format-as-contract`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to format as contract');
      }

      if (data.success && data.data?.content) {
        console.log('✅ [UnifiedWindow] Contract formatted successfully:', {
          hasContent: !!data.data.content,
          hasPlaceholders: !!data.data.placeholders,
          placeholdersCount: data.data.placeholders?.length || 0,
          placeholders: data.data.placeholders
        });

        // Update editor with formatted content
        editorProps.editorRef.current.setContent(data.data.content);
        // Also call onSave if available to persist the change
        if (editorProps.onSave) {
          editorProps.onSave(data.data.content);
        }
        // Store contract data and show save buttons
        const contractDataToStore = {
          content: data.data.content,
          placeholders: data.data.placeholders || []
        };
        console.log('📦 [UnifiedWindow] Setting contract data:', contractDataToStore);
        setContractData(contractDataToStore);
        setIsContractFormatted(true);
        console.log('🎯 [UnifiedWindow] Contract state set - isContractFormatted:', true, 'contractData:', contractDataToStore);
        Alert.alert('Success', 'Content formatted as contract successfully! You can now save it to Document Templates or Knowledge Base.');
      } else {
        console.error('❌ [UnifiedWindow] Invalid response format:', data);
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      console.error('Error formatting as contract:', error);
      Alert.alert('Error', error.message || 'Failed to format content as contract');
    } finally {
      setFormattingLoading(null);
    }
  };

  const handleFormatAsKnowledge = async () => {
    if (!editorProps?.editorRef?.current) {
      Alert.alert('Error', 'Editor is not available');
      return;
    }

    try {
      setFormattingLoading('knowledge');
      const content = editorProps.editorRef.current.getContent();

      if (!content || content.trim().length < 10) {
        Alert.alert('Error', 'Editor content is too short. Please add some content first.');
        setFormattingLoading(null);
        return;
      }

      const apiBaseUrl = await getApiBaseUrl();
      const response = await fetch(`${apiBaseUrl}/ai/editor/format-as-knowledge`, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ content }),
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.error || 'Failed to format as knowledge base item');
      }

      if (data.success && data.data?.content) {
        // Update editor with formatted content
        editorProps.editorRef.current.setContent(data.data.content);
        // Also call onSave if available to persist the change
        if (editorProps.onSave) {
          editorProps.onSave(data.data.content);
        }
        Alert.alert('Success', 'Content formatted as knowledge base item successfully!');
      } else {
        throw new Error('Invalid response format');
      }
    } catch (error: any) {
      console.error('Error formatting as knowledge:', error);
      Alert.alert('Error', error.message || 'Failed to format content as knowledge base item');
    } finally {
      setFormattingLoading(null);
    }
  };

  const handleSaveToDocumentTemplate = async () => {
    if (!contractData || !contractData.content) {
      Alert.alert('Error', 'No contract content to save');
      return;
    }

    try {
      setSaving('template');

      // Extract title from content or use default
      const titleMatch = contractData.content.match(/<h1[^>]*>(.*?)<\/h1>/i);
      const title = titleMatch ? titleMatch[1].replace(/<[^>]*>/g, '').trim() : 'Contract Template';

      // Extract description from first paragraph
      const descMatch = contractData.content.match(/<p[^>]*>(.*?)<\/p>/i);
      const description = descMatch ? descMatch[1].replace(/<[^>]*>/g, '').trim().substring(0, 200) : 'Contract document template with placeholders';

      const saveResponse = await postDocumentTemplate({
        title: title,
        description: description,
        content: contractData.content,
        tags: ['contract', 'template', 'ai-generated'],
        isTemplate: true,
        createdBy: currentUser?._id,
        data: {
          placeholders: contractData.placeholders,
          generatedBy: 'ai',
          metadata: {
            source: 'editor-format-as-contract',
            formattedAt: new Date().toISOString()
          }
        }
      });

      if (saveResponse?.data?.document?._id) {
        Alert.alert(
          'Success',
          'Contract saved to Document Templates!',
          [
            {
              text: 'View Template',
              onPress: () => {
                if (navigation) {
                  navigation.navigate('Data', {
                    screen: 'DocumentTemplates'
                  });
                }
              }
            },
            { text: 'OK' }
          ]
        );
        // Reset contract state
        setIsContractFormatted(false);
        setContractData(null);
      } else {
        throw new Error('Failed to save template');
      }
    } catch (error: any) {
      console.error('Error saving to document template:', error);
      Alert.alert('Error', error.message || 'Failed to save contract to document templates');
    } finally {
      setSaving(null);
    }
  };

  const handleSaveToKnowledge = async () => {
    if (!contractData || !contractData.content) {
      Alert.alert('Error', 'No contract content to save');
      return;
    }

    try {
      setSaving('knowledge');

      // Extract title from content or use default
      const titleMatch = contractData.content.match(/<h1[^>]*>(.*?)<\/h1>/i);
      const title = titleMatch ? titleMatch[1].replace(/<[^>]*>/g, '').trim() : 'Contract Document';

      await postInternalKnowledge({
        title: title,
        content: contractData.content,
        type: 'internal',
        description: 'Contract document formatted from editor content',
        category: 'contracts',
        tags: ['contract', 'document', 'ai-generated']
      }, (progressData) => {
        console.log('Knowledge creation progress:', progressData);
      }).then((id) => {
        if (id) {
          Alert.alert(
            'Success',
            'Contract saved to Knowledge Base!',
            [
              {
                text: 'View Article',
                onPress: () => {
                  if (navigation) {
                    navigation.navigate('Knowledge', {
                      screen: 'EditKnowledge',
                      params: { knowledgeArticleId: id }
                    });
                  }
                }
              },
              { text: 'OK' }
            ]
          );
          // Reset contract state
          setIsContractFormatted(false);
          setContractData(null);
        } else {
          throw new Error('Failed to save knowledge article');
        }
      });
    } catch (error: any) {
      console.error('Error saving to knowledge:', error);
      Alert.alert('Error', error.message || 'Failed to save contract to knowledge base');
    } finally {
      setSaving(null);
    }
  };

  const getWindowActions = () => {
    switch (activeComponent) {
      case 'editor':
        const isFormattingContract = formattingLoading === 'contract';
        const isFormattingKnowledge = formattingLoading === 'knowledge';
        const isSavingTemplate = saving === 'template';
        const isSavingKnowledge = saving === 'knowledge';

        // Debug: Log condition evaluation
        console.log('🎨 [UnifiedWindow] getWindowActions - Editor case:', {
          isContractFormatted,
          hasContractData: !!contractData,
          shouldShowSaveButtons: isContractFormatted && !!contractData,
          activeComponent
        });

        // Show save buttons if contract is formatted
        if (isContractFormatted && contractData) {
          return (
            <YStack flex={3} gap={8} style={{ backgroundColor: 'transparent', zIndex: 10 }}>
              <XStack justifyContent="center" alignItems="center" gap={10}>
                <Pressable
                  onPress={handleSaveToDocumentTemplate}
                  disabled={isSavingTemplate || isSavingKnowledge}
                  style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: "row",
                    gap: 8,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    borderRadius: 8,
                    backgroundColor: theme ? '#3b82f6' : '#2563eb',
                    opacity: (isSavingTemplate || isSavingKnowledge) ? 0.5 : 1
                  }}
                >
                  {isSavingTemplate ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <MaterialIcons name="description" size={16} color="#ffffff" />
                  )}
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#ffffff' }}>
                    {isSavingTemplate ? 'Saving...' : 'Save to Document Templates'}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={handleSaveToKnowledge}
                  disabled={isSavingTemplate || isSavingKnowledge}
                  style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexDirection: "row",
                    gap: 8,
                    paddingHorizontal: 12,
                    paddingVertical: 8,
                    borderRadius: 8,
                    backgroundColor: theme ? '#10b981' : '#059669',
                    opacity: (isSavingTemplate || isSavingKnowledge) ? 0.5 : 1
                  }}
                >
                  {isSavingKnowledge ? (
                    <ActivityIndicator size="small" color="#ffffff" />
                  ) : (
                    <MaterialIcons name="library-books" size={16} color="#ffffff" />
                  )}
                  <Text style={{ fontSize: 12, fontWeight: 'bold', color: '#ffffff' }}>
                    {isSavingKnowledge ? 'Saving...' : 'Save to Knowledge Base'}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => {
                    setIsContractFormatted(false);
                    setContractData(null);
                  }}
                  style={{
                    alignItems: 'center',
                    justifyContent: 'center',
                    paddingHorizontal: 8,
                    paddingVertical: 8,
                  }}
                >
                  <AntDesign name="close" size={16} color={theme ? '#6b7280' : '#9ca3af'} />
                </Pressable>
              </XStack>
              <Text style={{
                fontSize: 10,
                color: theme ? '#6b7280' : '#9ca3af',
                textAlign: 'center',
                fontStyle: 'italic'
              }}>
                Contract formatted with {contractData.placeholders.length} placeholder{contractData.placeholders.length !== 1 ? 's' : ''}
              </Text>
            </YStack>
          );
        }

        // Show format buttons when not formatted
        return <XStack flex={3} justifyContent="center" alignItems="center" gap={10} style={{ backgroundColor: 'transparent', zIndex: 10 }}>
          <Pressable
            onPress={handleFormatAsContract}
            disabled={isFormattingContract || isFormattingKnowledge}
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: "row",
              gap: 20,
              opacity: (isFormattingContract || isFormattingKnowledge) ? 0.5 : 1
            }}
          >
            {isFormattingContract ? (
              <ActivityIndicator size="small" color={theme ? '#1a1a1a' : '#ffffff'} />
            ) : (
              <FontAwesome6 name="file-contract" size={16} color={theme ? '#1a1a1a' : '#ffffff'} />
            )}
            <Text style={{ fontSize: 12, fontWeight: 'bold', color: theme ? '#1a1a1a' : '#ffffff' }}>
              {isFormattingContract ? 'Formatting...' : 'Format as Contract'}
            </Text>
          </Pressable>
          <Pressable
            onPress={handleFormatAsKnowledge}
            disabled={isFormattingContract || isFormattingKnowledge}
            style={{
              alignItems: 'center',
              justifyContent: 'center',
              flexDirection: "row",
              gap: 20,
              opacity: (isFormattingContract || isFormattingKnowledge) ? 0.5 : 1
            }}
          >
            {isFormattingKnowledge ? (
              <ActivityIndicator size="small" color={theme ? '#1a1a1a' : '#ffffff'} />
            ) : (
              <FontAwesome6 name="file-pdf" size={16} color={theme ? '#1a1a1a' : '#ffffff'} />
            )}
            <Text style={{ fontSize: 12, fontWeight: 'bold', color: theme ? '#1a1a1a' : '#ffffff' }}>
              {isFormattingKnowledge ? 'Formatting...' : 'Format as Knowledge Base Item'}
            </Text>
          </Pressable>
        </XStack>
    }
  }

  // Always render the Animated.View for animation to work
  // When activeComponent is null, widthAnim will be 0, making the window invisible
  return (
    <Animated.View
      style={containerStyles as any}
      pointerEvents={activeComponent ? 'auto' : 'none'}
    >
      {activeComponent && (
        <>
          {/* Unified Window Header - Outside ErrorBoundary so it doesn't disappear on error */}
          <View style={getWindowHeaderStyles(theme)}>
            <View style={{ flex: 1, marginRight: 8 }}>
              <Text style={getWindowHeaderTitleStyles(theme)}>
                {getWindowTitle()}
              </Text>
              {getWindowActions()}
            </View>
            <Pressable
              onPress={onClose}
              style={getWindowCloseButtonStyles()}
            >
              <AntDesign
                name="close"
                size={18}
                color={getWindowCloseIconColor(theme)}
              />
            </Pressable>
          </View>

          {/* Window Content - Lazy Load Component - Wrapped in ErrorBoundary */}
          <ErrorBoundary 
            id={activeComponent || 'window'} 
            theme={theme}
            onReload={() => {
              // Force remount by resetting component loaded state
              setComponentLoaded(null);
            }}
          >
            <View style={{ flex: 1, position: 'relative', overflow: 'hidden' }}>
              {isLoading || !componentLoaded ? (
                <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                  <Loading text={`Loading ${getWindowTitle()}...`} />
                </View>
              ) : (
                <React.Suspense fallback={
                  <View style={{ flex: 1, justifyContent: 'center', alignItems: 'center', padding: 40 }}>
                    <Loading text={`Loading ${getWindowTitle()}...`} />
                  </View>
                }>
                  {activeComponent === 'hierarchy' && hierarchyProps && (
                    <HierarchyWindow {...hierarchyProps} contentOnly />
                  )}
                  {activeComponent === 'canvas' && canvasProps && (
                    <CanvasWindow {...canvasProps} contentOnly />
                  )}
                  {activeComponent === 'editor' && editorProps && (
                    <EditorWindow {...editorProps} contentOnly />
                  )}
                  {activeComponent === 'dataHandler' && dataHandlerProps && (
                    <DataHandlerWindow {...dataHandlerProps} contentOnly />
                  )}
                    {activeComponent === 'document' && documentProps && (
                      <DocumentWindow {...documentProps} contentOnly />
                    )}
                </React.Suspense>
              )}
            </View>
          </ErrorBoundary>
        </>
      )}
    </Animated.View>
  );
};

export default UnifiedWindow;


